import type { ChatConfig, PrivacyGateTexts } from '../types/chat'

export interface ResolvedPrivacyTexts {
  title: string
  intro: string
  sensitiveDataNote: string
  privacyLinkText: string
  privacyPolicyUrl?: string
  acceptButton: string
}

/**
 * Built-in copy for the privacy gate. German and English are shipped by
 * default; integrators can add/override locales via `privacy.texts`.
 */
export const PRIVACY_DEFAULT_TEXTS: Record<string, Required<Omit<PrivacyGateTexts, 'privacyPolicyUrl'>>> = {
  de: {
    title: 'Hinweis zum KI-Chat',
    intro: 'Sie nutzen einen KI-gestützten Chat.',
    sensitiveDataNote: 'Bitte geben Sie keine sensiblen Daten ein.',
    privacyLinkText: 'Datenschutz',
    acceptButton: 'Chat starten',
  },
  en: {
    title: 'AI chat notice',
    intro: 'You are using an AI-powered chat.',
    sensitiveDataNote: 'Please do not enter any sensitive data.',
    privacyLinkText: 'Privacy policy',
    acceptButton: 'Start chat',
  },
}

/**
 * Resolves the gate texts for a locale: exact match → language-prefix match →
 * English fallback for the defaults, with user-provided `texts` taking
 * precedence field by field. The privacy-policy URL falls back from a
 * locale-specific override to the top-level `privacy.privacyPolicyUrl`.
 */
export function resolvePrivacyTexts(locale: string, config: ChatConfig): ResolvedPrivacyTexts {
  const privacy = config.privacy ?? {}
  const prefix = (locale || '').split('-')[0]

  const base = PRIVACY_DEFAULT_TEXTS[locale] ?? PRIVACY_DEFAULT_TEXTS[prefix] ?? PRIVACY_DEFAULT_TEXTS.en

  const userTexts = privacy.texts ?? {}
  const user: PrivacyGateTexts = userTexts[locale] ?? userTexts[prefix] ?? {}

  return {
    title: user.title ?? base.title,
    intro: user.intro ?? base.intro,
    sensitiveDataNote: user.sensitiveDataNote ?? base.sensitiveDataNote,
    privacyLinkText: user.privacyLinkText ?? base.privacyLinkText,
    privacyPolicyUrl: user.privacyPolicyUrl ?? privacy.privacyPolicyUrl,
    acceptButton: user.acceptButton ?? base.acceptButton,
  }
}
